import { useEffect, useState } from "react"

import { Button } from "@coval/ui"
import { useEditorState, type Editor } from "@tiptap/react"
import {
  Bold,
  ChevronDown,
  Columns2,
  Italic,
  List,
  ListOrdered,
  SeparatorHorizontal,
  Underline,
} from "lucide-react"

import type {
  NOTEBOOK_FONTS,
  NotebookAppearance,
} from "../utils/notebookCanvas"

export function NotebookCanvasToolbar({
  editor,
  appearance,
  onAppearance,
  disabled,
}: {
  editor: Editor | null
  appearance: NotebookAppearance
  onAppearance: (value: NotebookAppearance) => void
  disabled: boolean
}) {
  const [defaults, setDefaults] = useState<NotebookAppearance>({})
  useEffect(() => {
    const theme = getComputedStyle(document.documentElement)
    setDefaults({
      color: theme.getPropertyValue("--coval-text").trim(),
      background: theme.getPropertyValue("--surface-default").trim(),
    })
  }, [])
  const paragraphStyle = useEditorState({
    editor,
    selector: ({ editor: current }) =>
      current?.isActive("heading")
        ? String(current.getAttributes("heading").level)
        : "paragraph",
  })
  return (
    <div
      className="sticky top-0 z-10 mx-auto flex max-w-6xl flex-wrap items-center gap-1 rounded-lg border border-coval-border bg-surface-default p-2 text-sm text-coval-text"
      role="toolbar"
      aria-label="Notebook formatting"
    >
      <Button
        icon
        variant="ghost"
        tooltip="Insert slide break"
        disabled={disabled}
        onClick={() => editor?.chain().focus().setHorizontalRule().run()}
      >
        <SeparatorHorizontal />
      </Button>
      <select
        aria-label="Paragraph style"
        className="max-w-36 rounded bg-surface-default p-1"
        disabled={disabled}
        value={paragraphStyle ?? "paragraph"}
        onChange={(event) => {
          const value = Number(event.target.value)
          if (value) {
            editor
              ?.chain()
              .focus()
              .setHeading({ level: value as 1 | 2 | 3 })
              .run()
          } else {
            editor?.chain().focus().setParagraph().run()
          }
        }}
      >
        <option value="paragraph">Paragraph</option>
        <option value="1">Title</option>
        <option value="2">Heading</option>
        <option value="3">Subheading</option>
      </select>
      <select
        aria-label="Document font"
        className="max-w-32 rounded bg-surface-default p-1"
        disabled={disabled}
        value={appearance.font ?? "sans"}
        onChange={(event) =>
          onAppearance({
            ...appearance,
            font: event.target.value as keyof typeof NOTEBOOK_FONTS,
          })
        }
      >
        <option value="sans">Sans serif</option>
        <option value="serif">Serif</option>
        <option value="mono">Monospace</option>
      </select>
      <select
        aria-label="Document text size"
        className="rounded bg-surface-default p-1"
        disabled={disabled}
        value={appearance.fontSize ?? 16}
        onChange={(event) =>
          onAppearance({ ...appearance, fontSize: Number(event.target.value) })
        }
      >
        {Array.from({ length: 21 }, (_, index) => index + 12).map((size) => (
          <option key={size}>{size}</option>
        ))}
      </select>
      <Button
        icon
        variant="ghost"
        tooltip="Bold"
        disabled={disabled}
        onClick={() => editor?.chain().focus().toggleBold().run()}
      >
        <Bold />
      </Button>
      <Button
        icon
        variant="ghost"
        tooltip="Italic"
        disabled={disabled}
        onClick={() => editor?.chain().focus().toggleItalic().run()}
      >
        <Italic />
      </Button>
      <Button
        icon
        variant="ghost"
        tooltip="Underline"
        disabled={disabled}
        onClick={() => editor?.chain().focus().toggleUnderline().run()}
      >
        <Underline />
      </Button>
      <Button
        icon
        variant="ghost"
        tooltip="Bullet list"
        disabled={disabled}
        onClick={() => editor?.chain().focus().toggleBulletList().run()}
      >
        <List />
      </Button>
      <Button
        icon
        variant="ghost"
        tooltip="Numbered list"
        disabled={disabled}
        onClick={() => editor?.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered />
      </Button>
      <Button
        icon
        variant="ghost"
        tooltip="Insert two columns"
        disabled={disabled}
        onClick={() =>
          editor
            ?.chain()
            .focus()
            .insertContent({
              type: "columns",
              content: [
                { type: "column", content: [{ type: "paragraph" }] },
                { type: "column", content: [{ type: "paragraph" }] },
              ],
            })
            .run()
        }
      >
        <Columns2 />
      </Button>
      <Button
        icon
        variant="ghost"
        tooltip="Insert collapsible content"
        disabled={disabled}
        onClick={() =>
          editor
            ?.chain()
            .focus()
            .insertContent({
              type: "details",
              content: [
                {
                  type: "detailsSummary",
                  content: [{ type: "text", text: "More details" }],
                },
                {
                  type: "detailsContent",
                  content: [
                    {
                      type: "paragraph",
                      content: [{ type: "text", text: "Write here" }],
                    },
                  ],
                },
              ],
            })
            .run()
        }
      >
        <ChevronDown />
      </Button>
      <label className="ml-1 flex items-center gap-1">
        Text
        <input
          type="color"
          className="h-6 w-7 cursor-pointer bg-transparent"
          aria-label="Document text color"
          disabled={disabled}
          value={appearance.color ?? defaults.color ?? ""}
          onChange={(event) =>
            onAppearance({ ...appearance, color: event.target.value })
          }
        />
      </label>
      <label className="flex items-center gap-1">
        Page
        <input
          type="color"
          className="h-6 w-7 cursor-pointer bg-transparent"
          aria-label="Page background color"
          disabled={disabled}
          value={appearance.background ?? defaults.background ?? ""}
          onChange={(event) =>
            onAppearance({ ...appearance, background: event.target.value })
          }
        />
      </label>
      {(appearance.color ||
        appearance.background ||
        appearance.font ||
        appearance.fontSize) && (
        <Button
          variant="ghost"
          disabled={disabled}
          onClick={() => onAppearance({})}
        >
          Reset style
        </Button>
      )}
    </div>
  )
}
